import React, {useReducer, forwardRef, useImperativeHandle} from 'react'
import {Alert, Button, H6, Modal, ModalActions} from '../../UI/index.js'
import {formSpacing, FormRow, Input} from '../../UI/forms/index.js'
import {Reseller, OrderProduct} from '../../../../server/functions/resellers/resellers.types.js'
import {Customer} from '../../../../server/functions/customers/customers.types.js'
import t from '../../../helpers/Languages.js'
import Languages from '@utils/Languages.js'
import OrderConditionGroupsEditor from '../../conditions/OrderConditionGroupsEditor.js'

interface EditOrderProductModalProps {
    reseller: Reseller
    customers: {[customerId: string]: Customer}
    onChange: (orderProduct: OrderProduct) => void
}

interface EditOrderProductModalState {
    modalIsOpen: boolean
    orderProduct?: OrderProduct
    shippingOptions: string[]
    loading: boolean
    error: string
    validation: {
        name: boolean
    }
}

export interface EditOrderProductModalRef {
    open: (orderProduct: OrderProduct) => void
}

const EditOrderProductModal = forwardRef<EditOrderProductModalRef, EditOrderProductModalProps>((props, ref) => {
    const {reseller, customers, onChange} = props

    const initialState: EditOrderProductModalState = {
        modalIsOpen: false,
        orderProduct: undefined,
        shippingOptions: [],
        loading: false,
        error: '',
        validation: {
            name: false
        }
    }

    const [state, setState] = useReducer((state: EditOrderProductModalState, newState: Partial<EditOrderProductModalState>) => ({...state, ...newState}), structuredClone(initialState))

    useImperativeHandle(ref, () => ({open}))

    const open = (orderProduct: OrderProduct) => {
        // Ensure conditionGroups is initialized
        if (!orderProduct?.conditionGroups) {
            orderProduct.conditionGroups = []
        }
        if (!orderProduct?.name) {
            setTimeout(() => {
                document.getElementById('orderProductName')?.focus()
            }, 10)
        }
        setState({modalIsOpen: true, orderProduct})
    }

    const close = () => {
        setState(initialState)
    }

    const save = () => {
        if (!validate()) {
            return
        }
        if (state.orderProduct) {
            onChange(state.orderProduct)
        }
        close()
    }

    const validate = () => {
        let valid = true
        let focusId = ''
        let error = ''
        let validation = {name: false}

        if (!state.orderProduct?.name) {
            valid = false
            error = t('errorsNameRequired')
            focusId = 'orderProductName'
            validation.name = true
        }

        setState({error, validation})

        if (!valid && focusId) {
            document.getElementById(focusId)?.focus()
        }

        return valid
    }

    return (
        <Modal
            style={{maxWidth: 800}}
            show={state.modalIsOpen}
            title={state.orderProduct?.id ? `Bewerk product` : 'Nieuw product'}
            onClose={close}
            closeButton
        >
            <FormRow style={{marginBottom: formSpacing.rowGap}}>
                <Input
                    id='orderProductName'
                    label={Languages.name['NL']}
                    value={state.orderProduct?.name}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setState({orderProduct: {...state.orderProduct!, name: event.target.value}})
                    }}
                    isInvalid={state.validation.name}
                />
            </FormRow>

            <H6 style={{marginBottom: 12, marginTop: 36}}>{t('conditions')}</H6>

            {state.orderProduct &&
                <OrderConditionGroupsEditor
                    conditionGroups={state.orderProduct.conditionGroups}
                    matchAll={state.orderProduct.matchAllConditions}
                    customers={customers}
                    reseller={reseller}
                    onChange={(conditionGroups, matchAllConditions) => setState({orderProduct: {...state.orderProduct!, conditionGroups, matchAllConditions}})}
                />
            }

            {state.error && <Alert style={{marginBottom: 6}} variant='danger'>{state.error}</Alert>}

            <ModalActions style={{marginTop: 12}}>
                <Button
                    style={{marginTop: 24}}
                    onClick={save}
                    loading={state.loading}
                    label={t('save')}
                />
            </ModalActions>
        </Modal>
    )
})

EditOrderProductModal.displayName = 'EditOrderProductModal'

export default EditOrderProductModal
